import { useCallback, useEffect, useState } from 'react';
import { Container } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { ApiError } from '@/lib/api';
import type { Catalogue } from '@/lib/intake/catalogue';
import { type ApplicationView, intake } from '@/lib/intake/api';
import { type Problem, toProblem } from '@/lib/problem';
import { ApplicantLayout } from './ApplicantLayout';
import { Journey } from './Journey';
import { LandingStep } from './LandingStep';
import styles from './Journey.module.css';

type Loaded =
  | { kind: 'loading' }
  | { kind: 'failed'; problem: Problem }
  | { kind: 'none'; catalogue: Catalogue }
  | { kind: 'ready'; catalogue: Catalogue; application: ApplicationView; startOnLanding: boolean };

/**
 * The applicant's page. Loads the questionnaire and their application; with no
 * application yet, the landing step's Start creates one, records consent and opens
 * the first section in one go.
 */
export function ApplyPage() {
  const [loaded, setLoaded] = useState<Loaded>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      intake.catalogue(),
      intake.current().catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }),
    ])
      .then(([catalogue, application]) => {
        if (cancelled) return;
        setLoaded(
          application === null
            ? { kind: 'none', catalogue }
            : { kind: 'ready', catalogue, application, startOnLanding: true },
        );
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoaded({ kind: 'failed', problem: toProblem(error) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Creates the draft and, in the same breath, records consent and opens section 1. */
  const begin = useCallback(async (catalogue: Catalogue) => {
    setBusy(true);
    setProblem(null);
    try {
      let created: ApplicationView;
      try {
        created = await intake.create();
      } catch (error) {
        // Started in another tab a moment ago: carry on with that one.
        if (error instanceof ApiError && error.code === 'application_exists' && error.body?.latest) {
          created = error.body.latest as ApplicationView;
        } else {
          throw error;
        }
      }
      const opened = await intake.save(created.applicationId, {
        version: created.version,
        currentStep: 1,
        answers: {},
        consent: true,
      });
      setLoaded({ kind: 'ready', catalogue, application: opened, startOnLanding: false });
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setBusy(false);
    }
  }, []);

  const discard = useCallback(
    async (catalogue: Catalogue, application: ApplicationView, restart: boolean) => {
      setBusy(true);
      setProblem(null);
      try {
        await intake.clear(application.applicationId);
        if (restart) {
          await begin(catalogue);
        } else {
          setLoaded({ kind: 'none', catalogue });
        }
      } catch (error) {
        setProblem(toProblem(error));
      } finally {
        setBusy(false);
      }
    },
    [begin],
  );

  return (
    <ApplicantLayout>
      <Container>
        {problem && (
          <div className={styles.column}>
            <ProblemAlert problem={problem} title="That did not work" />
          </div>
        )}
        {loaded.kind === 'failed' && (
          <div className={styles.column}>
            <ProblemAlert problem={loaded.problem} title="Your application could not be loaded" />
          </div>
        )}
        {loaded.kind === 'none' && (
          <div className={styles.column}>
            <LandingStep
              headingRef={{ current: null }}
              sections={loaded.catalogue.sections.length}
              consented={false}
              anyComplete={false}
              busy={busy}
              onStart={() => void begin(loaded.catalogue)}
              onContinue={() => undefined}
            />
          </div>
        )}
        {loaded.kind === 'ready' && (
          <Journey
            key={loaded.application.applicationId}
            catalogue={loaded.catalogue}
            initial={loaded.application}
            startOnLanding={loaded.startOnLanding}
            busy={busy}
            onStartOver={() => void discard(loaded.catalogue, loaded.application, true)}
            onClear={() => void discard(loaded.catalogue, loaded.application, false)}
          />
        )}
      </Container>
    </ApplicantLayout>
  );
}
